/**
 * Admin Blog API Service
 */

import { get, post, patch, del } from '../client';
import { PaginatedResponse } from '../../../types';

export interface AdminPost {
  id: string;
  userId: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  status: 'draft' | 'published' | 'archived';
  seoTitle: string | null;
  seoDescription: string | null;
  seoKeywords: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: string;
    fullName: string;
    email: string;
  };
  categories?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  tags?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  medias?: Array<{
    id: string;
    fileUrl: string;
    fileType: string;
  }>;
}

export interface CreatePostRequest {
  title: string;
  content: string;
  excerpt?: string;
  status?: 'draft' | 'published' | 'archived';
  categoryIds?: string[];
  tagIds?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
}

export interface UpdatePostRequest extends Partial<CreatePostRequest> { }

function mapMedia(media: any) {
  return {
    id: String(media.id || ''),
    fileUrl: String(media.fileUrl || media.file_url || ''),
    fileType: String(media.fileType || media.file_type || 'image'),
  };
}

function mapPost(post: any): AdminPost {
  return {
    id: String(post.id || ''),
    userId: String(post.userId || post.user_id || ''),
    title: String(post.title || ''),
    slug: String(post.slug || ''),
    excerpt: post.excerpt ?? null,
    content: String(post.content || ''),
    status: (post.status || 'draft') as 'draft' | 'published' | 'archived',
    seoTitle: post.seoTitle ?? post.seo_title ?? null,
    seoDescription: post.seoDescription ?? post.seo_description ?? null,
    seoKeywords: post.seoKeywords ?? post.seo_keywords ?? null,
    publishedAt: post.publishedAt ?? post.published_at ?? null,
    createdAt: String(post.createdAt || post.created_at || ''),
    updatedAt: String(post.updatedAt || post.updated_at || ''),
    author: post.author
      ? {
        id: String(post.author.id || ''),
        fullName: String(post.author.fullName || post.author.full_name || ''),
        email: String(post.author.email || ''),
      }
      : undefined,
    categories: Array.isArray(post.categories)
      ? post.categories.map((category: any) => ({
        id: String(category.id || ''),
        name: String(category.name || ''),
        slug: String(category.slug || ''),
      }))
      : [],
    tags: Array.isArray(post.tags)
      ? post.tags.map((tag: any) => ({
        id: String(tag.id || ''),
        name: String(tag.name || ''),
        slug: String(tag.slug || ''),
      }))
      : [],
    medias: Array.isArray(post.medias) ? post.medias.map(mapMedia) : [],
  };
}

/**
 * Get all posts
 */
export async function getAdminPosts(params: { page?: number; limit?: number } = {}): Promise<PaginatedResponse<AdminPost>> {
  const response = await get<any>('/admin/posts', params);
  return {
    ...response,
    data: Array.isArray(response.data) ? response.data.map(mapPost) : [],
  };
}

/**
 * Get single post
 */
export async function getAdminPost(id: string): Promise<AdminPost> {
  const response = await get<{ data: any }>(`/admin/posts/${id}`);
  return mapPost(response.data);
}

/**
 * Create new post
 */
export async function createAdminPost(data: CreatePostRequest): Promise<AdminPost> {
  const response = await post<{ data: any }>('/admin/posts', data);
  return mapPost(response.data);
}

/**
 * Update post
 */
export async function updateAdminPost(id: string, data: UpdatePostRequest): Promise<AdminPost> {
  const response = await patch<{ data: any }>(`/admin/posts/${id}`, data);
  return mapPost(response.data);
}

/**
 * Delete post
 */
export async function deleteAdminPost(id: string): Promise<void> {
  await del(`/admin/posts/${id}`);
}

/**
 * Publish post
 */
export async function publishAdminPost(id: string): Promise<AdminPost> {
  const response = await post<{ data: any }>(`/admin/posts/${id}/publish`);
  return mapPost(response.data);
}

/**
 * Archive post
 */
export async function archiveAdminPost(id: string): Promise<AdminPost> {
  const response = await post<{ data: any }>(`/admin/posts/${id}/archive`);
  return mapPost(response.data);
}

/**
 * Upload post media
 */
export async function uploadPostMedia(postId: string, file: File): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);

  return await post(`/admin/posts/${postId}/medias`, formData);
}

/**
 * Get all categories
 */
export async function getAdminCategories(): Promise<any> {
  return await get('/admin/categories');
}
